import { ReactNode, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { SelectChangeEvent } from '@mui/material';
import {
  GridRenderCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid/models';

import {
  FormMultiSelectField as BaseFormMultiSelectField,
  FormSelectField as BaseFormSelectField,
} from '@sbv-clients/ui-forms';
import {
  Box,
  CircularProgress,
  MultiSelect as BaseMultiSelect,
  Select as BaseSelect,
} from '@sbv-clients/ui-shared';
import { Density } from '@sbv-clients/util-misc';

type Options<TMeta = any> = {
  id: string | number;
  label?: ReactNode;
  value: string | number;
  inActive?: boolean;
  disabled?: boolean;
  meta?: TMeta;
}[];

export type BaseFieldProps<TMeta = any> = {
  autoSelectIfEmpty?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  value?: string | number;
  label?: string;
  sx?: object;
  name?: string;
  hideStartAdornment?: boolean;
  required?: boolean;
  disabled?: boolean;
  onChange?: (
    e: SelectChangeEvent<unknown>,
    child: ReactNode,
    meta: TMeta
  ) => void;
  applyDataGridInputStyles?: boolean;
  defaultOpen?: boolean;
  notSelectedLabel?: string;
  withAllOption?: boolean;
  extraOptions?: Options<TMeta>;
  density?: Density;
  active?: boolean;
  hideInactive?: boolean;
  clearable?: boolean;
  onClear?: () => void;
};

const ALL_OPTIONS_VALUE = -1 as const;

function useValidationOptions<TMeta = any>({
  options,
  value,
  isSuccess,
  setValue,
  autoSelectIfEmpty,
}: {
  options: Options<TMeta>;
  name?: string;
  value?: string | number;
  isSuccess?: boolean;
  setValue?: (value: any, child: ReactNode, meta: TMeta | undefined) => void;
  autoSelectIfEmpty?: boolean;
}) {
  useEffect(() => {
    if (!isSuccess || !setValue) return;
    const hasValue = value !== undefined && value !== null && value !== '';

    if (!hasValue) {
      if (autoSelectIfEmpty) {
        const option = options.find((opt) => !opt.inActive && !opt.disabled);
        if (option?.value !== undefined) {
          setValue(option.value, '', option.meta);
        }
      }
    } else {
      const existsInOptions = options.some((opt) => opt.value === value);
      if (!existsInOptions) {
        setValue(null, '', {} as TMeta);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, options]);
}

function addAllOptionIfNeeded(withAllOption?: boolean): Options {
  if (!withAllOption) return [];
  return [
    {
      id: ALL_OPTIONS_VALUE,
      label: 'All',
      value: ALL_OPTIONS_VALUE,
    },
  ];
}

function mergeOptions(
  withAllOption?: boolean,
  extraOptions?: Options,
  fetchedOptions?: Options
): Options {
  const allOption = addAllOptionIfNeeded(withAllOption);
  return [
    ...(allOption || []),
    ...(extraOptions || []),
    ...(fetchedOptions || []),
  ];
}

function applyHideInactive(
  options: Options,
  hideInactive?: boolean,
  currentValue?: string | number | (string | number)[]
): Options {
  if (!hideInactive) return options;
  return options.filter((opt) => {
    if (!opt.inActive) return true;
    if (Array.isArray(currentValue)) return currentValue.includes(opt.value);
    return opt.value === currentValue;
  });
}

function useOptionsFieldCommon<
  TFieldProps extends BaseFieldProps<TMeta>,
  THookParams,
  TMeta = any
>({
  props,
  useOptionsHook,
  extractHookParams,
  setValue,
  currentValue,
}: {
  props: TFieldProps;
  useOptionsHook: (params: THookParams) => {
    options: Options<TMeta>;
    isSuccess?: boolean;
    isLoading?: boolean;
  };
  extractHookParams: (props: TFieldProps) => THookParams;
  setValue?: (value: any, child: ReactNode, meta: TMeta | undefined) => void;
  currentValue?: string | number;
}) {
  const {
    options: fetchedOptions,
    isSuccess,
    isLoading,
  } = useOptionsHook(extractHookParams(props));
  const finalOptions = useMemo(() => {
    const merged = mergeOptions(
      props.withAllOption,
      props.extraOptions,
      fetchedOptions
    );
    return applyHideInactive(merged, props.hideInactive, currentValue);
  }, [
    fetchedOptions,
    props.extraOptions,
    props.withAllOption,
    props.hideInactive,
    currentValue,
  ]);

  useValidationOptions({
    options: finalOptions,
    name: props.name,
    value: props.value,
    isSuccess,
    setValue,
    autoSelectIfEmpty: props.autoSelectIfEmpty,
  });

  return { options: finalOptions, isSuccess, isLoading };
}

export type BaseMultiFieldProps = Omit<BaseFieldProps, 'value' | 'onChange'> & {
  value?: (string | number)[];
  onChange?: (
    event: SelectChangeEvent<(string | number)[]>,
    child: ReactNode
  ) => void;
};

function useMultiValidationOptions({
  options,
  values,
  isSuccess,
  setValue,
}: {
  options: Options;
  values?: (string | number)[];
  isSuccess?: boolean;
  setValue?: (value: (string | number)[]) => void;
}) {
  useEffect(() => {
    if (!isSuccess || !setValue || !values?.length) return;
    const filtered = values.filter((v) =>
      options.some((opt) => opt.value === v)
    );
    if (filtered.length !== values.length) {
      setValue(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, options]);
}

function useMultiOptionsFieldCommon<THookParams>({
  props,
  useOptionsHook,
  extractHookParams,
  setValue,
  currentValue,
}: {
  props: BaseMultiFieldProps & THookParams;
  useOptionsHook: (params: THookParams) => {
    options: Options;
    isSuccess?: boolean;
    isLoading?: boolean;
  };
  extractHookParams: (props: any) => THookParams;
  setValue?: (value: (string | number)[]) => void;
  currentValue?: (string | number)[];
}) {
  const {
    options: fetchedOptions,
    isSuccess,
    isLoading,
  } = useOptionsHook(extractHookParams(props));
  const finalOptions = useMemo(() => {
    const merged = mergeOptions(
      props.withAllOption,
      props.extraOptions,
      fetchedOptions
    );
    return applyHideInactive(
      merged,
      props.hideInactive,
      currentValue ?? props.value
    );
  }, [
    fetchedOptions,
    props.extraOptions,
    props.withAllOption,
    props.hideInactive,
    props.value,
    currentValue,
  ]);

  useMultiValidationOptions({
    options: finalOptions,
    values: props.value,
    isSuccess,
    setValue,
  });

  return { options: finalOptions, isSuccess, isLoading };
}

type FieldPropsWithMeta<TFieldProps extends BaseFieldProps, TMeta> = Omit<
  TFieldProps,
  'onChange' | 'extraOptions'
> &
  BaseFieldProps<TMeta>;

export function createOptionsFieldBuilder<
  TFieldProps extends BaseFieldProps,
  THookParams,
  TPrefix extends string,
  TRowType extends GridValidRowModel = GridValidRowModel,
  TMeta = any
>({
  prefix,
  useOptionsHook,
  extractHookParams,
  useLabelHook,
}: {
  prefix: TPrefix;
  useOptionsHook: (params: THookParams) => {
    options: Options<TMeta>;
    isSuccess?: boolean;
    isLoading?: boolean;
  };
  extractHookParams: (props: TFieldProps) => THookParams;
  useLabelHook?: () => string | undefined;
}) {
  const useLabel = useLabelHook ?? (() => undefined);

  const SelectField = (props: FieldPropsWithMeta<TFieldProps, TMeta>) => {
    const dynamicLabel = useLabel();
    const { options, isLoading } = useOptionsFieldCommon<
      FieldPropsWithMeta<TFieldProps, TMeta>,
      THookParams,
      TMeta
    >({
      props,
      useOptionsHook,
      extractHookParams: extractHookParams as any,
      setValue: (value, child, meta) =>
        props.onChange?.(
          { target: { value } } as SelectChangeEvent<unknown>,
          child,
          meta as TMeta
        ),
      currentValue: props.value,
    });
    return (
      <BaseSelect
        {...props}
        label={dynamicLabel ?? props.label}
        isLoading={isLoading}
        options={options}
        onClear={() =>
          props.onChange?.(
            { target: { value: null } } as SelectChangeEvent<unknown>,
            null,
            undefined as TMeta
          )
        }
      />
    );
  };

  const FormSelectField = (props: FieldPropsWithMeta<TFieldProps, TMeta>) => {
    const dynamicLabel = useLabel();
    const watchedValue = useWatch({ name: props.name ?? '' });
    const { options, isLoading } = useOptionsFieldCommon<
      FieldPropsWithMeta<TFieldProps, TMeta>,
      THookParams,
      TMeta
    >({
      props,
      useOptionsHook,
      extractHookParams: extractHookParams as any,
      setValue: (value, child, meta) =>
        props.onChange?.(
          {
            target: { value, name: props.name },
          } as SelectChangeEvent<unknown>,
          child,
          meta as TMeta
        ),
      currentValue: watchedValue,
    });
    return (
      <BaseFormSelectField
        {...props}
        label={dynamicLabel ?? props.label}
        isLoading={isLoading}
        options={options}
      />
    );
  };

  const DataGridSelectField = (
    props: GridRenderCellParams<TRowType> &
      THookParams & {
        withAllOption?: boolean;
        extraOptions?: Options<TMeta>;
        hideInactive?: boolean;
        onChange?: (
          e: SelectChangeEvent<unknown>,
          child: ReactNode,
          meta: TMeta
        ) => void;
      }
  ) => {
    const { options } = useOptionsFieldCommon({
      props: props as any,
      useOptionsHook,
      extractHookParams,
      setValue: (value) => {
        props.api.setEditCellValue({ id: props.id, field: props.field, value });
      },
      currentValue: props.value as string | number | undefined,
    });

    const fieldProps = {
      fullWidth: true,
      size: 'small' as const,
      defaultOpen: true,
      notSelectedLabel: '',
      applyDataGridInputStyles: true,
      value: props.value,
    };

    return (
      <BaseSelect
        {...(props as any)}
        {...(fieldProps as Partial<BaseFieldProps>)}
        options={options}
        onChange={(event, child, meta) => {
          if (props?.onChange) {
            props?.onChange?.(event, child, meta);
          }
          if (props.hasFocus) {
            props.api.setEditCellValue({
              id: props.id,
              field: props.field,
              value: event.target.value,
            });
            props.api.stopCellEditMode({ id: props.id, field: props.field });
          }
        }}
        onClose={() =>
          setTimeout(() => {
            if (props.api.getCellMode(props.id, props.field) === 'edit') {
              props.api.stopCellEditMode({ id: props.id, field: props.field });
            }
          })
        }
      />
    );
  };

  type MultiFieldProps = BaseMultiFieldProps & THookParams;

  const MultiSelectField = (props: MultiFieldProps) => {
    const { options, isLoading } = useMultiOptionsFieldCommon({
      props,
      useOptionsHook,
      extractHookParams,
      setValue: (value) =>
        props.onChange?.(
          { target: { value } } as SelectChangeEvent<(string | number)[]>,
          null
        ),
      currentValue: props.value,
    });
    return (
      <BaseMultiSelect
        {...(props as any)}
        isLoading={isLoading}
        options={options}
      />
    );
  };

  const FormMultiSelectField = (props: MultiFieldProps) => {
    const watchedValue = useWatch({ name: props.name ?? '' });
    const { options, isLoading } = useMultiOptionsFieldCommon({
      props,
      useOptionsHook,
      extractHookParams,
      currentValue: watchedValue,
    });
    return (
      <BaseFormMultiSelectField
        {...(props as any)}
        isLoading={isLoading}
        options={options}
      />
    );
  };

  const MultiSelectIdToLabelRenderer = (
    props: THookParams & {
      value: (string | number)[];
      withAllOption?: boolean;
      extraOptions?: Options;
    }
  ) => {
    const { value, withAllOption, extraOptions, ...rest } = props;
    const { options: fetchedOptions = [], isLoading } = useOptionsHook(
      rest as THookParams
    );
    const finalOptions = mergeOptions(
      withAllOption,
      extraOptions,
      fetchedOptions
    );
    if (isLoading) {
      return (
        <Box>
          <CircularProgress size={10} />
        </Box>
      );
    }
    const labels = value
      .map((v) => finalOptions.find((opt) => opt.value === v)?.label)
      .filter(Boolean)
      .join(', ');
    return <>{labels}</>;
  };

  const IdToLabelRenderer = (
    props: THookParams & {
      value: string | number;
      withAllOption?: boolean;
      extraOptions?: Options;
    }
  ) => {
    const { value, withAllOption, extraOptions, ...rest } = props;
    const { options: fetchedOptions = [], isLoading } = useOptionsHook(
      rest as THookParams
    );
    const finalOptions = mergeOptions(
      withAllOption,
      extraOptions,
      fetchedOptions
    );
    if (isLoading) {
      return (
        <Box>
          <CircularProgress size={10} />
        </Box>
      );
    }
    const label = finalOptions.find((opt) => opt.value === value)?.label;
    return <>{label}</>;
  };

  return {
    [`${prefix}SelectField`]: SelectField,
    [`${prefix}FormSelectField`]: FormSelectField,
    [`${prefix}DataGridSelectField`]: DataGridSelectField,
    [`${prefix}IdToLabelRenderer`]: IdToLabelRenderer,
    [`${prefix}MultiSelectField`]: MultiSelectField,
    [`${prefix}FormMultiSelectField`]: FormMultiSelectField,
    [`${prefix}MultiSelectIdToLabelRenderer`]: MultiSelectIdToLabelRenderer,
  } as {
    [K in
      | `${TPrefix}SelectField`
      | `${TPrefix}FormSelectField`
      | `${TPrefix}DataGridSelectField`
      | `${TPrefix}IdToLabelRenderer`
      | `${TPrefix}MultiSelectField`
      | `${TPrefix}FormMultiSelectField`
      | `${TPrefix}MultiSelectIdToLabelRenderer`]: K extends `${TPrefix}SelectField`
      ? typeof SelectField
      : K extends `${TPrefix}FormSelectField`
      ? typeof FormSelectField
      : K extends `${TPrefix}DataGridSelectField`
      ? typeof DataGridSelectField
      : K extends `${TPrefix}IdToLabelRenderer`
      ? typeof IdToLabelRenderer
      : K extends `${TPrefix}MultiSelectField`
      ? typeof MultiSelectField
      : K extends `${TPrefix}FormMultiSelectField`
      ? typeof FormMultiSelectField
      : typeof MultiSelectIdToLabelRenderer;
  };
}
